/**LAGOSReports — /state/lagos/reports — Institutional Report Library */
import React, { useState } from "react";
import { useLagos } from "@/context/LAGOSContext";
import { FileText, Download, Loader, Clock, Shield, BarChart2, DollarSign, Globe } from "lucide-react";
import { toast } from "sonner";

const fmtN = (v, u = "") => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K ${u}`.trim(); return `${n.toLocaleString()} ${u}`.trim(); };

const REPORT_GROUPS = [
  {
    id: "portfolio",
    title: "Portfolio Reports",
    icon: BarChart2,
    color: "#059669",
    description: "State-level portfolio: emissions, reduction, sector and LGA analysis",
    reports: [
      {
        key: "state-portfolio",
        label: "Lagos State Portfolio Report",
        badge: "Primary",
        badgeC: "bg-emerald-100 text-emerald-700",
        pages: "~10",
        desc: "State-level portfolio: 74 entities, emissions baseline, reduction potential, sector breakdown, LGA heatmap, CPA/PoA analysis. Full navy cover, QR-verified.",
        ssot: true,
      },
      {
        key: "sdg-eesg",
        label: "SDG & EESG Impact Report",
        badge: "Impact",
        badgeC: "bg-amber-100 text-amber-700",
        pages: "~3",
        desc: "6 SDGs addressed. EESG framework: Environmental, Economic, Social, Governance. OECD Rio Marker 2 alignment.",
        ssot: true,
      },
    ],
  },
  {
    id: "structuring",
    title: "Structuring Reports",
    icon: Globe,
    color: "#1d4ed8",
    description: "CPA/PoA programme architecture, Article 6.4 pathway, eligibility",
    reports: [
      {
        key: "carbon-structuring",
        label: "CPA/PoA Carbon Structuring Report",
        badge: "Structuring",
        badgeC: "bg-blue-100 text-blue-700",
        pages: "~9",
        desc: "CPA groupings, PoA programme architecture, Article 6.4 pathway, VCM eligibility, additionality signals, issuance timeline.",
        ssot: true,
      },
    ],
  },
  {
    id: "finance",
    title: "Finance & Green Bond Reports",
    icon: DollarSign,
    color: "#7c3aed",
    description: "ICMA-aligned Green Bond Framework, capital allocation, impact reporting",
    reports: [
      {
        key: "green-bond-framework",
        label: "Green Bond Framework",
        badge: "ICMA GBP 2021",
        badgeC: "bg-purple-100 text-purple-700",
        pages: "~14",
        desc: "ICMA Core Components: Use of Proceeds, Project Evaluation, Proceeds Management, Reporting. Full governance & verification timeline. QR-verified.",
        ssot: true,
        financeParam: true,
      },
      {
        key: "allocation-report",
        label: "Allocation Report",
        badge: "Use of Proceeds",
        badgeC: "bg-blue-100 text-blue-700",
        pages: "~6",
        desc: "Entity-level allocation by ICMA category. Ring-fenced proceeds management. Sector and LGA breakdown.",
        ssot: true,
        financeParam: true,
      },
      {
        key: "impact-report",
        label: "Impact Report",
        badge: "Climate Impact",
        badgeC: "bg-emerald-100 text-emerald-700",
        pages: "~5",
        desc: "Annual climate outcomes: tCO₂e reduction, circularity, SDG contribution, LGA geographic spread. IFRS S2 aligned.",
        ssot: true,
        financeParam: true,
      },
    ],
  },
  {
    id: "disclosure",
    title: "Disclosure & Registry Reports",
    icon: Shield,
    color: "#0891b2",
    description: "Public eco-label register, QR verification",
    reports: [
      {
        key: "eco-label-register",
        label: "Eco Label Register",
        badge: "Public Disclosure",
        badgeC: "bg-cyan-100 text-cyan-700",
        pages: "~3",
        desc: "All issued eco-labels with QR verification codes, entity details, validity dates, scores. Suitable for public disclosure.",
        ssot: true,
      },
    ],
  },
];

export default function LAGOSReports() {
  const { selectedPeriod, periods, setSelectedPeriod, portfolio } = useLagos();
  const [downloading, setDownloading] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({ portfolio: true, structuring: true, finance: true, disclosure: true });

  const handleDownload = async (key, financeParam) => {
    if (!selectedPeriod) return;
    setDownloading(key);
    const params = financeParam ? "?debt_pct=60&equity_pct=25&conc_pct=15&tenor_yr=7" : "";
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/lagos/pdf/${key}/${selectedPeriod}${params}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("decarboniq_token")}` } }
      );
      if (!r.ok) throw new Error(r.statusText);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Lagos-${key}-${selectedPeriod}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch { toast.error("Report generation failed"); }
    finally { setDownloading(null); }
  };

  const totalReports = REPORT_GROUPS.reduce((s, g) => s + g.reports.length, 0);
  const now = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
            Institutional Report Library
          </span>
          <h1 className="text-2xl font-black text-slate-900 mt-1.5">Reports</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Dynamic · QR-verifiable · SSOT-driven · Full navy cover · Institutional grade
          </p>
        </div>
        <select value={selectedPeriod || ""} onChange={e => setSelectedPeriod(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white" data-testid="period-selector">
          {periods.map(p => <option key={p.period_id} value={p.period_id}>{p.label}</option>)}
        </select>
      </div>

      {/* Summary Banner */}
      <div className="bg-[#0f172a] rounded-xl p-5 text-white">
        <p className="text-[9px] font-bold uppercase tracking-widest opacity-40 mb-3">Report Library Status</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            ["Total Reports",       String(totalReports),                                "#94a3b8"],
            ["Entities in Scope",   fmtN(portfolio.total_entities || 74),                "#34d399"],
            ["Reporting Period",    selectedPeriod || "—",                                "#94a3b8"],
            ["Last Refresh",        now,                                                  "#94a3b8"],
          ].map(([k, v, c]) => (
            <div key={k}>
              <p className="text-[9px] opacity-40 uppercase tracking-wider">{k}</p>
              <p className="text-sm font-black mt-0.5" style={{ color: c }}>{v}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-white/10">
          <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded">SSOT-driven: all values from live platform</span>
          <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded">QR verification enabled</span>
          <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded">Institutional navy cover</span>
          <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded">Hash-linked audit trail</span>
        </div>
      </div>

      {/* Report Groups */}
      {REPORT_GROUPS.map(group => {
        const Icon = group.icon;
        const isExpanded = expandedGroups[group.id];
        return (
          <div key={group.id} className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
              onClick={() => setExpandedGroups(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
              data-testid={`report-group-${group.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${group.color}15` }}>
                  <Icon size={16} style={{ color: group.color }} />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800">{group.title}</p>
                  <p className="text-[10px] text-slate-400">{group.description} · {group.reports.length} report{group.reports.length !== 1 ? "s" : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: `${group.color}15`, color: group.color }}>
                  {group.reports.length}
                </span>
                <Clock size={13} className={isExpanded ? "rotate-180 text-slate-400" : "text-slate-400"} style={{ transform: isExpanded ? "rotate(90deg)" : "none" }} />
              </div>
            </button>

            {isExpanded && (
              <div className="border-t border-slate-100 divide-y divide-slate-50">
                {group.reports.map(r => (
                  <div
                    key={r.key}
                    className="flex items-start justify-between gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
                    data-testid={`report-${r.key}`}
                  >
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-9 h-9 bg-slate-50 border border-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText size={15} className="text-slate-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <p className="text-sm font-bold text-slate-800">{r.label}</p>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.badgeC}`}>{r.badge}</span>
                          <span className="text-[9px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{r.pages} pages</span>
                        </div>
                        <p className="text-xs text-slate-500">{r.desc}</p>
                        <div className="flex gap-2 mt-1.5">
                          {r.ssot && (
                            <span className="text-[9px] bg-emerald-50 text-emerald-600 border border-emerald-100 px-1.5 py-0.5 rounded font-medium">
                              SSOT-verified
                            </span>
                          )}
                          <span className="text-[9px] bg-slate-50 text-slate-500 border border-slate-100 px-1.5 py-0.5 rounded">
                            QR enabled
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(r.key, r.financeParam)}
                      disabled={downloading === r.key || !selectedPeriod}
                      className="flex items-center gap-2 bg-[#1d4ed8] text-white text-xs px-4 py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-60 flex-shrink-0 min-w-[130px] justify-center font-bold"
                      data-testid={`download-${r.key}`}
                    >
                      {downloading === r.key
                        ? <><Loader size={11} className="animate-spin" /> Generating…</>
                        : <><Download size={11} /> Download PDF</>
                      }
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
